import React, { useState, useEffect, useCallback } from 'react';
import MetaTags from "react-meta-tags";
import ApiClient from '../../BaseUrl/ApiClient';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Input,
  FormGroup,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from 'reactstrap';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faPencil, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useParams, useHistory } from 'react-router-dom';
import { formatTimeDisplay } from './timeUtils';

/* debounce helper */
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

function HappyHours() {
  const { businessId } = useParams();
  const history = useHistory();

  const [deals, setDeals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, totalDocs: 0, limit: 10 });
  const [selectedCategory, setSelectedCategory] = useState('');

  const [deleteModal, setDeleteModal] = useState(false);
  const [dealToDelete, setDealToDelete] = useState(null);

  const [imageModal, setImageModal] = useState(false);
  const [modalImages, setModalImages] = useState([]);

  const toggleDeleteModal = () => setDeleteModal(!deleteModal);
  const toggleImageModal = () => setImageModal(!imageModal);

  const openImageModal = (images) => {
    setModalImages(images);
    setImageModal(true);
  };

  // Fetch categories for dropdown
  const fetchCategories = async () => {
    try {
      const response = await ApiClient().get(`/admin/categories`);
      setCategories(response.data.responseData?.categories || []);
    } catch (error) {
      toast.error("Failed to fetch categories");
    }
  };

  // Fetch deals from backend
  const fetchDeals = async (page = 1, limit = pagination.limit, categoryId = selectedCategory) => {
    if (!businessId) return;
    setIsLoading(true);

    try {
      const params = {
        page: page.toString(),
        limit: limit.toString()
      };
      if (categoryId) params.categoryId = categoryId;

      const response = await ApiClient().get(`/admin/deal/happyHours/${businessId}/list`, { params });
      const { responseData } = response.data;

      setDeals(responseData.deals || []);
      setPagination(responseData.pagination || { page: 1, totalPages: 1, totalDocs: 0, limit: 10 });
    } catch (error) {
      toast.error(error.response?.data?.responseMessage?.[0] || "Failed to fetch deals");
    } finally {
      setIsLoading(false);
    }
  };

  const debouncedFetchDeals = useCallback(
    debounce((page, limit, categoryId) => fetchDeals(page, limit, categoryId), 500),
    [pagination.limit, businessId, selectedCategory]
  );

  useEffect(() => {
    fetchCategories();
    fetchDeals();
  }, [businessId]);

  const handleCategoryChange = (e) => {
    const categoryId = e.target.value;
    setSelectedCategory(categoryId);
    debouncedFetchDeals(1, pagination.limit, categoryId);
  };

  const confirmDelete = (deal) => {
    setDealToDelete(deal);
    toggleDeleteModal();
  };

  const handleDelete = async () => {
    if (!dealToDelete) return;
    try {
      await ApiClient().delete(`/admin/deal/happyHours/${dealToDelete._id}`);
      toast.success("Deal deleted successfully");
      fetchDeals();
    } catch (error) {
      toast.error(error.response?.data?.responseMessage?.[0] || "Failed to delete deal");
    } finally {
      setDealToDelete(null);
      toggleDeleteModal();
    }
  };

  const handleUpdate = (dealId) => {
    history.push(`/update-happy-hours/${dealId}`);
  };

  const toggleActiveStatus = async (deal) => {
    const newStatus = deal.isActive ? "false" : "true";
    try {
      await ApiClient().patch(`/admin/deal/happyHours/${deal._id}/${newStatus}`);
      toast.success(`Deal ${deal.isActive ? 'deactivated' : 'activated'} successfully`);
      fetchDeals();
    } catch (error) {
      toast.error(error.response?.data?.responseMessage?.[0] || "Failed to update status");
    }
  };


  return (
    <React.Fragment>
      <ToastContainer />
      <MetaTags><title>Happy Hours</title></MetaTags>

      <Container fluid className="mt-3">
        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h2 className="card-title mb-0">Happy Hours Deals</h2>
                <Button color="primary" onClick={() => history.push(`/add-happy-hours/${businessId}`)}>
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Add Happy Hour
                </Button>
              </CardHeader>

              <CardBody>
                <Row className="justify-content-end mb-3">
                  <Col md={4}>
                    <FormGroup>
                      <Input type="select" value={selectedCategory} onChange={handleCategoryChange}>
                        <option value="">All Categories</option>
                        {categories.map(cat => (
                          <option key={cat._id} value={cat._id}>{cat.name}</option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                </Row>

                <div className="table-responsive">
                  <table className="table table-bordered table-striped">
                    <thead>
                      <tr>
                        <th>Deal Photo</th>
                        <th>Category</th>
                        <th>Days</th>
                        <th>Start Time</th>
                        <th>End Time</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading ? (
                        <tr><td colSpan="7" className="text-center">Loading...</td></tr>
                      ) : deals.length ? (
                        deals.map(deal => (
                          <tr key={deal._id}>
                            <td>
                              {deal.dealPhoto?.length > 0 ? (
                                <div className="position-relative" style={{ display: 'inline-block', cursor: 'pointer' }}>
                                  <img
                                    src={deal.dealPhoto[0].url}
                                    alt="deal"
                                    width="50"
                                    height="50"
                                    style={{ objectFit: 'cover' }}
                                    onClick={() => openImageModal(deal.dealPhoto)}
                                  />
                                  {deal.dealPhoto.length > 1 && (
                                    <span className="badge bg-dark position-absolute top-0 end-0" style={{ fontSize: '0.7em' }}>
                                      +{deal.dealPhoto.length - 1}
                                    </span>
                                  )}
                                </div>
                              ) : 'No Photo'}
                            </td>
                            <td>{deal.category?.name || '-'}</td>
                            <td>{deal.days?.join(", ") || '-'}</td>
                            <td>{formatTimeDisplay(deal.timeRange?.startTime, deal.timezone)}</td>
                            <td>{formatTimeDisplay(deal.timeRange?.endTime, deal.timezone)}</td>
                            <td>
                              <span className={`badge ${deal.isActive ? 'bg-success' : 'bg-danger'}`} style={{ padding: '0.4em 0.8em', borderRadius: '50px' }}>
                                {deal.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td>
                              <Button size="sm" className="me-2" onClick={() => history.push(`/happy-hours-detail/${deal._id}`)}>View</Button>
                              <Button color="primary" size="sm" className="me-2" onClick={() => handleUpdate(deal._id)}>
                                <FontAwesomeIcon icon={faPencil} />
                              </Button>
                              <Button color="danger" size="sm" className="me-2" onClick={() => confirmDelete(deal)}>
                                <FontAwesomeIcon icon={faTrash} />
                              </Button>
                              <Button color={deal.isActive ? "warning" : "success"} size="sm" className="me-2" onClick={() => toggleActiveStatus(deal)}>
                                {deal.isActive ? "Deactivate" : "Activate"}
                              </Button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr><td colSpan="7" className="text-center">No deals found</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Delete Modal */}
        <Modal isOpen={deleteModal} toggle={toggleDeleteModal}>
          <ModalHeader toggle={toggleDeleteModal}>Confirm Delete</ModalHeader>
          <ModalBody>Are you sure you want to delete <strong>{dealToDelete?.category?.name}</strong>?</ModalBody>
          <ModalFooter>
            <Button color="danger" onClick={handleDelete}>Delete</Button>
            <Button color="secondary" onClick={toggleDeleteModal}>Cancel</Button>
          </ModalFooter>
        </Modal>

        {/* Image Modal */}
        <Modal isOpen={imageModal} toggle={toggleImageModal} size="lg"  centered>
          <ModalHeader toggle={toggleImageModal}>Deal Images</ModalHeader>
          <ModalBody>
            <div className="d-flex flex-wrap gap-2">
              {modalImages.map((img, idx) => (
                <img key={idx} src={img.url} alt={`deal-${idx}`} width="150" style={{ objectFit: 'cover' }} />
              ))}
            </div>
          </ModalBody>
        </Modal>
      </Container>
    </React.Fragment>
  );
}

export default HappyHours;
